import { StyleConfig } from './layer.model';
import { BillingCycle } from './organization.model';
import { Price } from './price.model';
import { Subscription } from './user.model';

export interface FeatureDefinition {
  id: string;
  code: string;
  name: string;
  category: string;
  geometryType: 'Point' | 'LineString' | 'Polygon';
  style: StyleConfig;
  url?: string | { url: string; name: string };
  displayOnlyName?: boolean;
  payment?: number;
  paymentUnit?: string;
  emission?: number;
  emissionUnit?: string;
  habitatClassification?: {
    biodiversity?: boolean;
    productive?: boolean;
    distinctiveness?: 'VERY_HIGH' | 'HIGH' | 'MEDIUM' | 'LOW' | 'VERY_LOW';
  };
  description?: string;
  [key: string]: unknown;
}

export enum AttributeDefinitionType {
  AREA = 'area',
  PERIMETER = 'perimeter',
  LENGTH = 'length',
  INPUT = 'input',
  DATE = 'date',
  URL = 'url',
  DROPDOWN = 'dropdown',
  CHIP_LIST = 'chipList',
  URL_LABELED = 'urlLabeled',
  CODE_KEY = 'codeKey',
  CODE_NAME = 'codeName',
  FIELD_ID = 'fieldId',
  SOURCE = 'source',
  MOM_SOURCE = 'momSource',
  EMISSION = 'emission',
  EMISSION_VALUE = 'emissionValue',
  CODE_EMISSION = 'codeEmission',
  CONDITION_SCORE = 'conditionScore',
  LINKED_PLANS = 'linkedPlans',
  PAYMENT_RATE = 'paymentRate',
  PAYMENT_VALUE = 'paymentValue',
  GUIDANCE = 'guidance',
  PRICE_DESCRIPTION = 'priceDescription',
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
  HABITAT_COVER = 'habitatCover',
}

export interface AttributeDefinition {
  source: 'VIRTUAL' | 'PROPERTIES';
  type: AttributeDefinitionType;
  key: string;
  name: string;
  description?: string;
  featureCodes?: string[];
  keyboard?: string;
  minValue?: number;
  maxValue?: number;
  geometry?: GeoJSON.GeoJsonGeometryTypes[];
  options?: { label: string; value: string }[];
  hidden?: boolean;
  canDelete?: boolean;
  value?: unknown;
  keyName?: string;
  keyUrl?: string;
}

export interface ProjectTemplateRegionConfig {
  allRegions?: boolean;
  selectedRegions?: {
    region: string;
    allSubregions?: boolean;
    selectedSubregions?: string[];
  }[];
}

export enum ProjectDefinitionAvailability {
  PUBLIC = 'public',
  ORGANISATION = 'organisation',
  NONE = 'none',
}

export interface ProjectTemplate {
  id: string;
  name: string;
  description: string;
  type: 'plan' | 'survey';
  availability: ProjectDefinitionAvailability;
  data: FeatureDefinition[];
  active: boolean;
  beta?: boolean;
  readonly?: boolean;
  boundary?: boolean;
  about?: {
    title: string;
    link: string;
  };
  duplicateOnly?: boolean;
  attributes?: AttributeDefinition[];
  hiddenAttributes?: string[];
  secondaryData?: SecondaryCode[];
  necessaryAttributes?: string[];
  additionalAttributes?: {
    unitOptions?: { [key: string]: unknown };
    codeEmissions?: { [key: string]: number };
    [key: string]: unknown;
  };
  attributeNamesMapper?: { key: string; name: string }[];
  regionConfig: ProjectTemplateRegionConfig;
}

export interface SecondaryCode {
  id: string;
  name: string;
  category: string;
}

export interface Sector {
  id: string;
  name: string;
  templates: {
    key: string;
    suggested?: boolean;
  }[];
}

export interface OrganisationType {
  id: string;
  name: string;
}

export interface OrganisationType {
  id: string;
  name: string;
}

export interface SubscriptionsPrices {
  cycles: {
    [key in BillingCycle]: {
      [key in Subscription]: Price;
    };
  };
  discounts: {
    workflows: {
      [key: string]: {
        [key in Subscription]: number;
      };
    };
    credits: {
      [key in Subscription]: number;
    };
    prints: {
      [key in Subscription]: number;
    };
  };
  referrals: {
    forReferee: number;
    forReferrer: number;
  };
}

export enum RegionType {
  REGION = 'region',
  SUBREGION = 'subregion',
}

export interface Region {
  id: string;
  name: string;
  projection: string;
  extent?: [number, number, number, number];
  viewExtent?: [number, number, number, number];
  center: number[];
  default?: boolean;
  countries?: string[];
  type: RegionType;
  subregions?: string[];
}

export interface RegionsResponse {
  regions: Region[];
  availableRegionIds: string[];
}

export enum ImportMethodType {
  LAND_PICKER = 'LAND_PICKER',
  IMPORT_FROM_RURAL_PAYMENTS_ENGLAND = 'IMPORT_FROM_RURAL_PAYMENTS_ENGLAND',
  IMPORT_FILE = 'IMPORT_FILE',
  CREATE_FROM_EXISTING_PLAN = 'CREATE_FROM_EXISTING_PLAN',
  IMPORT_FROM_RURAL_PAYMENTS_WALES = 'IMPORT_FROM_RURAL_PAYMENTS_WALES',
  IMPORT_FROM_RURAL_PAYMENTS_SCOTLAND = 'IMPORT_FROM_RURAL_PAYMENTS_SCOTLAND',
}

export interface ImportMethodRegionConfig {
  allRegions?: boolean;
  selectedRegions?: {
    region: string;
    allSubregions?: boolean;
    selectedSubregions?: string[];
  }[];
}

export interface ImportMethod {
  type: ImportMethodType;
  layer?: string;
  title: string;
  description: string;
  regionConfig: ImportMethodRegionConfig;
  order: number;
  isMulti?: boolean;
  layerName?: string;
  planDefaultName?: string;
}
